"use client";

/**
 * Declaratively fetch recently deleted items.
 */

import React, { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import styled from "styled-components";
import { LingoError } from "../../constants/lingoError";
import { AnyObject } from "../../models";
import ActivityIndicator from "../ActivityIndicator";
import Box from "../Box";
import Button from "../Button";
import { ElementPositioningProps, SpacingValue } from "../ComponentProps";
import Flex from "../Flex";
import Notice from "../Notice";

const StyledButton = styled(Button).attrs({
  buttonStyle: "secondary",
  px: "xl",
  variations: {
    "mq.xs": {
      width: "100%",
    },
  },
})``;

const ButtonWrapper = styled(Box).attrs({
  width: "100%" as SpacingValue,
  textAlign: "center",
})``;

type Props = {
  hasMore: boolean;
  data: AnyObject[];
  error: LingoError;
  isLoading: boolean;
  fetchNextPage: () => void;
} & ElementPositioningProps;

/**
 * Rendered at the bottom of an infinite scroll list to show a loading state or error for pagination status.
 *
 * This is meant to be used with queryActions. It will show a loading state, an error state, or a button to load more data.
 *
 * Note: The component will not render anything no data has been provided. You should render the initial loading state in the parent component, typically a full page loader.
 *
 * Example:
 * ```
 * const totalCount = kit.counts.items // example
 * const {data, error, isLoading, fetchNextPage} = useData({});
 * const hasMore = data.length < totalCount;
 *
 *   if (totalCount === 0) {
 *     return (
 *       <EmptyState
 *         title="Nothing to see here"
 *         subtitle="There are no items to display."
 *       />
 *     );
 *   } else if (isLoading && !data?.length) {
 *     return <ActivityIndicator mt="xxl" center />;
 *   } else if (loadingError && !items?.length) {
 *     return (
 *       <EmptyState
 *         title="Unable to load data"
 *         subtitle={loadingError.message}
 *         iconProps={{ iconId: "error", fill: "error" }}
 *         button={{ text: "Retry", onClick: fetchNextPage }}
 *       />
 *     );
 *   }
 *   return (
 *     <>
 *       <ListComponent data={data} />
 *       {hasMore && (
 *         <PagingIndicator
 *           hasMore={hasMore}
 *           data={items}
 *           error={loadingError}
 *           fetchNextPage={fetchNextPage}
 *           isLoading={isLoading}
 *         />
 *       )}
 *     </>
 *   );
 * ```
 *
 * @param props
 * @returns
 */
const PagingIndicator: React.FC<Props> = ({
  data,
  hasMore,
  error,
  isLoading,
  fetchNextPage,
  ...rest
}) => {
  const [ref, inView] = useInView();

  // Fetching if inView and hasMoreAssets
  useEffect(() => {
    if (inView && !isLoading && !error && hasMore) {
      fetchNextPage();
    }
  }, [error, fetchNextPage, hasMore, inView, isLoading]);

  if (!data) return null;
  if (!hasMore) return null;
  if (isLoading) {
    return <ActivityIndicator mt="xxl" {...rest} center />;
  }
  if (error) {
    return (
      <ButtonWrapper ref={ref} mt="xxl" {...rest}>
        <Flex width="100%" justifyContent="center">
          <Notice
            width=""
            message={error.message}
            noticeStyle="error"
            button={{ text: "Try again", onClick: fetchNextPage }}
          />
        </Flex>
      </ButtonWrapper>
    );
  }
  return (
    <ButtonWrapper mt="xxl" ref={ref} {...rest}>
      <StyledButton id="load-more-button" text="Load more" onClick={fetchNextPage} />
    </ButtonWrapper>
  );
};

export default PagingIndicator;
